import parsePhoneNumber from 'libphonenumber-js/build/parsePhoneNumberFromString';
import {
  get,
  has,
  indexOf,
  isEmpty,
  pickBy,
  random,
  some,
  without,
  isFinite,
  last,
  omit,
  join,
  compact,
  isArray,
  flow,
  negate,
  isNil,
} from 'lodash';
import { pipe, defaultTo, evolve, prop, pathOr } from 'ramda';
import UrlPattern from 'url-pattern';
import moment from 'moment';
import {
  REQUEST_ERROR,
  BAD_ROUTE,
  TIME_WAITING_LIST,
  FAILED_BOOK_AND_RESERVE,
  FAILED_CHARGES,
  RESCHEDULE_INVALID_PROMO_ERROR,
  SHOP_TYPE,
  MAINTENANCE_MODE,
} from '../constants';
import config from '../../config';
import metadata from '../../metadata.min.json';

// Format price in cents
export const formatPrice = (num, currency = config.defaultCurrency, noDecimalsIfInt) => {
  const amount = num / 100;
  if (!isFinite(amount)) return null;

  let locale;
  switch (currency.toUpperCase()) {
    case 'CAD':
      locale = 'en-CA';
      break;
    case 'USD':
      locale = 'en-US';
      break;
    case 'GBP':
      locale = 'en-UK';
      break;
    case 'AUD':
      locale = 'en-AU';
      break;
    default:
      locale = 'en-US';
      break;
  }

  const isInteger = num % 100 === 0;
  const amountString = amount.toLocaleString(locale, {
    style: currency ? 'currency' : 'decimal',
    ...currency && { currency },
    minimumFractionDigits: (noDecimalsIfInt && isInteger) ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return `${amountString}`;
};

// Format phone number with dashes
export const formatPhone = (num) => {
  try {
    return parsePhoneNumber(num, metadata).formatInternational();
  } catch (e) {
    return num;
  }
};

// eslint-disable-next-line max-len
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidName = name => typeof name === 'string' && name.length > 1;

export const isValidLastName = name => typeof name === 'string' && name.length > 1;

export const isValidEmail = email => emailRegex.test(email);
export const isSameEmail = (email, emailToCheck) => {
  if (!email || !emailToCheck) {
    return false;
  }

  // consider web@getsquire.com and web+1@getsquire.com equal
  const plusPartRegex = /\+[\S\w]*@/ig;
  return email.replace(plusPartRegex, '@') === emailToCheck.replace(plusPartRegex, '@');
};

export const isValidPhone = (phone) => {
  if (!phone) {
    return false;
  }

  try {
    return parsePhoneNumber(phone, metadata).isPossible();
  } catch (_e) {
    return false;
  }
};

export const formatDuration = (mins) => {
  const duration = moment.duration(mins, 'minutes');
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  let daysHoursMins = '';
  if (days) {
    daysHoursMins += `${days} day`;
    if (days > 1) {
      daysHoursMins += 's';
    }
    daysHoursMins += ' ';
  }
  if (hours) {
    daysHoursMins += `${hours} hour`;
    if (hours > 1) {
      daysHoursMins += 's';
    }
    daysHoursMins += ' ';
  }
  if (minutes) {
    daysHoursMins += `${minutes} min`;
    if (minutes > 1) {
      daysHoursMins += 's';
    }
    daysHoursMins += ' ';
  }

  return daysHoursMins;
};

const pluralize = str => `${str}s`;
const suffixUnit = (value, unit) => (value > 1 ? `${value} ${pluralize(unit)}` : `${value} ${unit}`);
export const formatHours = (mins) => {
  const duration = moment.duration(mins, 'minutes');

  const getHours = () => {
    const hours = Math.floor(duration.as('hours'));
    if (!hours) return '';
    return suffixUnit(hours, 'hour');
  };

  const getMinutes = () => {
    const minutes = duration.minutes();
    if (!minutes) return '';
    return suffixUnit(minutes, 'minute');
  };

  return compact([getHours(), getMinutes()]).join(' ');
};

export const generateUrlString = (params, pattern = config.urlPatterns.old) => {
  const stringifyParams = Object.keys(params)
    .reduce((acc, key) => ({
      ...acc,
      [key]: params[key] ? params[key] : `:${key}`,
    }), {});

  const urlPattern = new UrlPattern(pattern);

  return urlPattern.stringify(stringifyParams);
};

const getUrlPatternLastParam = (params, pattern = config.urlPatterns.old) => {
  const urlPattern = new UrlPattern(pattern);
  return last(urlPattern.names);
};

export const getUrlPatternParamsOmitLast = (params, pattern = config.urlPatterns.old) =>
  omit(params, getUrlPatternLastParam(params, pattern));

export const getBarberUrls = urlPattern => ({
  barbers: generateUrlString({ shopRoute: '' }, urlPattern),
  services: generateUrlString({ shopRoute: '', barberRoute: '' }, urlPattern),
  pickTime: generateUrlString({ shopRoute: '', barberRoute: '', serviceId: '' }, urlPattern),
  makeReservation: generateUrlString({
    shopRoute: '',
    barberRoute: '',
    serviceId: '',
    reservationTime: '',
  }, urlPattern),
});

export const getTimeUrls = urlPattern => ({
  pickTime: generateUrlString({ shopRoute: '' }, urlPattern),
  barbers: generateUrlString({ shopRoute: '', reservationTime: '' }, urlPattern),
  services: generateUrlString({ shopRoute: '', reservationTime: '', barberRoute: '' }, urlPattern),
  makeReservation: generateUrlString({
    shopRoute: '',
    reservationTime: '',
    barberRoute: '',
    serviceId: '',
  }, urlPattern),
});

export const getServiceUrls = urlPattern => ({
  services: generateUrlString({ shopRoute: '' }, urlPattern),
  barbers: generateUrlString({ shopRoute: '', serviceId: '' }, urlPattern),
  pickTime: generateUrlString({ shopRoute: '', barberRoute: '', serviceId: '' }, urlPattern),
  makeReservation: generateUrlString({
    shopRoute: '',
    barberRoute: '',
    serviceId: '',
    reservationTime: '',
  }, urlPattern),
});

export const getGiftCardUrls = urlPattern => ({
  shopUrl: generateUrlString({ shopRoute: '' }, urlPattern),
  barberUrl: generateUrlString({ shopRoute: '', barberRoute: '' }, urlPattern),
  checkoutUrl: generateUrlString({
    shopRoute: '',
    barberRoute: '',
    giftCardAmount: '',
  }, urlPattern),
});

export const byId = (set, id) => set.find(item => item.get('id') === id);

export const indexById = (set, id) => set.findIndex(item => item.get('id') === id);

export const checkStatus = (response) => {
  if (response.ok) return response.json();

  return response.text().then((text) => {
    let json;
    if (text) json = JSON.parse(text);
    const message = get(json, 'message') || response.statusText;
    const code = get(json, 'code');
    const kind = get(json, 'kind');

    return Promise.reject({ message, code, kind });
  });
};

export const handleRequestError = dispatch => (error) => {
  if (error instanceof TypeError) {
    return Promise.reject();
  }

  if (!error) return Promise.reject();

  if (error.kind === 'only_prepay') {
    dispatch({ type: FAILED_CHARGES });
    return dispatch({
      type: FAILED_BOOK_AND_RESERVE,
      payload: error.message,
    });
  }

  const InvalidPromoErrorCode = 'RemoveInvalidPromoError';
  if (error.code === InvalidPromoErrorCode) {
    return dispatch({
      type: RESCHEDULE_INVALID_PROMO_ERROR,
      payload: error.message,
    });
  }

  if (error.code === 'ServiceUnavailable') {
    return dispatch({
      type: MAINTENANCE_MODE,
      payload: error.message,
    });
  }

  return dispatch({
    type: REQUEST_ERROR,
    payload: get(error, 'message'),
  });
};

export const checkRoute = (dispatch, params, urlPattern) => (response) => {
  // TODO: Remove 500 status check when service uuid error removed on API
  if (response.status === 404 || response.status === 500) {
    const redirect = generateUrlString(params, urlPattern);

    dispatch({
      type: BAD_ROUTE,
      payload: redirect || '/404',
    });

    return Promise.reject();
  }

  return response;
};

export const nullFunc = () => null;

export const randomItem = items => (isEmpty(items) ? items : items[random(items.length - 1)]);

export const toggleItemInArray = (items, item) =>
  (indexOf(items, item) !== -1 ? without(items, item) : [...items, item]);

export const parseTimeSlug = (time, timezone = 'utc') => {
  const parsed = moment(time, config.timeSlugFormat).tz(timezone);
  if (parsed && parsed.isValid()) return parsed;
  return null;
};

export const formatReservationTime = (time, timezone) => {
  if (time === TIME_WAITING_LIST) return '';
  const parsed = parseTimeSlug(time, timezone);
  if (!parsed) return '';

  return parsed.format(config.dateTimeFormatLong);
};

export const fireResizeEventOnWindow = () => {
  const resizeEvent = (() => {
    if (typeof (Event) !== 'function') {
      const evt = window.document.createEvent('UIEvents');
      evt.initUIEvent('resize', true, false, window, 0);
      return evt;
    }
    return new Event('resize');
  })();

  return window.dispatchEvent(resizeEvent);
};

export const formatAddress = ({ street, street2, city, state, zip } = {}) =>
  join(compact([street, street2, city, state, zip]), ', ');

export const transformShopAddress = pipe(
  shop => ({ ...shop, country: pathOr(null, ['address', 'country'], shop) }),
  evolve({
    address: formatAddress,
  })
);

export const transformShopLocation = evolve({
  location: pipe(
    prop('coordinates'),
    ([lat, lng]) => ({ lat, lng })
  ),
});

export const extendLocation = (location, params) => {
  if (typeof location === 'string') return { ...params, pathname: location };
  if (typeof location === 'object') return { ...location, ...params };
  return params;
};

export const xor = (a, b) => !a !== !b;

export const paramMissing = (keys, params) => some(keys, key => !(has(pickBy(params, negate(isNil)), key)));

export const mapTimes = (item) => {
  if (typeof item === 'string') return { time: item };
  return item;
};

export const calculatePercent = (value, percent) => (value * (percent / 100));

export const getStartOfDay = (day) => {
  const date = day ? new Date(day) : new Date();
  date.setUTCHours(0, 0, 0, 0);
  return date;
};

export const dayIsInRange = (day, from, to) => (day >= from && day <= to);

export const addDaysToDate = (date, days) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const arrayRotateRight = (arr, places = 1) => [
  ...arr.slice(-places),
  ...arr.slice(0, -places),
];

export const getPersonName = ({ firstName, lastName } = {}) =>
  ((firstName && lastName) ? `${firstName} ${lastName}` : '');

export const getReservationTimeFromTimeItem = ({ time } = {}) => time && moment(time).format(config.timeSlugFormat);

export const joinQueryStringParams = (params) => {
  if (!isArray(params)) {
    return '';
  }

  const paramsStr = params.filter(Boolean).join('&');
  if (!paramsStr) {
    return '';
  }

  return `?${paramsStr}`;
};

const getAvailableTimes = timezone => (() => {
  const today = moment.tz(timezone).startOf('day');
  const configIntervalInDays = moment.duration(config.availableTimesFetchInterval).days();

  const differenceByDays = flow(
    dateString => moment(dateString).diff(today),
    dateMomentOrDuration => moment.duration(dateMomentOrDuration).days(),
  );

  const addDaysToToday = days => moment.tz(today, timezone).add(days, 'days').format(config.apiDateFormat);

  const calculateStartDay = (startDay = today) => flow(
    differenceByDays,
    days => Math.floor(days / configIntervalInDays) * configIntervalInDays,
    addDaysToToday,
  )(startDay);

  const calculateEndDay = (startDay = today) => flow(
    differenceByDays,
    days => ((1 + Math.floor(days / configIntervalInDays)) * configIntervalInDays) - 1,
    addDaysToToday,
  )(startDay);

  return {
    startDay: calculateStartDay,
    endDay: calculateEndDay,
  };
})();

export const getAvailableTimesStartDay = timezone => getAvailableTimes(timezone).startDay;

export const getAvailableTimesEndDay = timezone => getAvailableTimes(timezone).endDay;

export const appointmentIsCancelable = (appointmentStart, advanceCancelMins) =>
    moment.utc(appointmentStart).diff(moment.utc(), 'minutes') >= advanceCancelMins;

export const joinSlash = (...args) => args.filter(Boolean).join('/');

export const ensureInt = pipe(
  v => parseInt(v, 10),
  defaultTo(0),
);

/* eslint-disable no-bitwise, prefer-template */
export const generateShortId = () => {
  let firstPart = (Math.random() * 46656) | 0;
  let secondPart = (Math.random() * 46656) | 0;
  firstPart = ('000' + firstPart.toString(36)).slice(-3);
  secondPart = ('000' + secondPart.toString(36)).slice(-3);
  return firstPart + secondPart;
};
/* eslint-enable no-bitwise, prefer-template */

export const isCommissionShop = shop => shop && shop.kind === SHOP_TYPE.commission;

export const wait = time => (
  new Promise((resolve) => {
    setTimeout(resolve, time);
  })
);

export const renderTurnstileChallenge = containerSelector => (
  new Promise((resolve) => {
    window.turnstile.render(containerSelector, {
      appearance: 'always',
      sitekey: config.botProtection.siteKey,
      callback: (token) => {
        resolve({ success: true, token });
      },
      'error-callback': () => {
        resolve({ success: false });
      },
    });
  })
);



// WEBPACK FOOTER //
// ./src/app/utils/helpers.js